/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { PinsDaoService } from '../pins-dao.service';
import {
  DbPersistenceService,
  Query
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { Pin } from 'src/app/_model/pin';
import { LocalPinDbTableDefinition } from './local-pin-db-table-definition';
import { PinDbTableDefinition } from './pin-db-table-definition';

@Injectable()
export class PinsDbDaoService implements PinsDaoService {

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private pinTableDefinition: PinDbTableDefinition,
    private localPinTableDefinition: LocalPinDbTableDefinition,
  ) { }

  public async savePins(pins: Pin[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var pinQueries: Query[] = [];
      for (const pin of pins) {
        let parameters;
        let values;
        let fields;
        let table;
        if (pin.isLocal()) {
          parameters = [
            pin.id.secondaryId,
            pin.databag ? JSON.stringify(pin.databag) : undefined,
            pin.extFileId,
            pin.pinTypeId,
            +pin.locationX,
            +pin.locationY,
            pin.page,
            pin.name,
            pin.description,
            pin.color,
            pin.timestampCreated,
            pin.createdBy
          ];
          values = '(?,?,?,?,?,?,?,?,?,?,?,?)';
          fields = '(id, databag, extfileid, pintypeid, locationx, locationy, page, name, description, color, entitycreatedtstamp, createdby, entitycreateddevicetoken, entitycreatedlocaldeviceid)';
          table = this.localPinTableDefinition.tableName;
        } else {
          parameters = [
            pin.id.imsId,
            pin.databag ? JSON.stringify(pin.databag) : undefined,
            pin.extFileId,
            pin.pinTypeId,
            +pin.locationX,
            +pin.locationY,
            pin.page,
            pin.name,
            pin.description,
            pin.color,
            pin.timestampCreated,
            pin.createdBy
          ];
          values = '(?,?,?,?,?,?,?,?,?,?,?,?,?,?)';
          fields = '(id, databag, extfileid, pintypeid, locationx, locationy, page, name, description, color, entitycreatedtstamp, createdby, entitycreateddevicetoken, entitycreatedlocaldeviceid)';
          table = this.pinTableDefinition.tableName;
        }
        let pinQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            table + ' ' + 
            fields +
            ' VALUES ' +
            values,
          parameters: parameters
        };
        pinQueries.push(pinQuery);
      }

      const result = await this.dbPersistenceService.executeBatch(db, pinQueries);
    } catch (error) {
      this.logger.log(
        'Error saving pins to db',
        error,
        '-',
        error?.message
      );
    }
  }

  public async createPin(pin: Pin): Promise<Pin> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let parameters = [
        pin.databag ? JSON.stringify(pin.databag) : undefined,
        pin.extFileId,
        pin.pinTypeId,
        +pin.locationX,
        +pin.locationY,
        pin.page,
        pin.name,
        pin.description,
        pin.color,
        pin.timestampCreated,
        pin.createdBy
      ];
      let values = '(?,?,?,?,?,?,?,?,?,?,?,?,?)';
      let fields = '(databag, extfileid, pintypeid, locationx, locationy, page, name, description, color, entitycreatedtstamp, createdby, entitycreateddevicetoken, entitycreatedlocaldeviceid)';
      let table = this.localPinTableDefinition.tableName;

      let pinQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          table + ' ' + 
          fields +
          ' VALUES ' +
          values,
        parameters: parameters
      };

      await this.dbPersistenceService.executeStatement(db, pinQuery);
      let res = await this.dbPersistenceService.executeQuery(db, {statement: "select last_insert_rowid() as id", parameters: []})
      if (res && res.values) {
        pin.id.secondaryId = res.values.shift()?.id;
      }
      return pin;
    } catch (error) {
      this.logger.log(
        'Error saving pins to db',
        error,
        '-',
        error?.message
      );
      return undefined
    }
  }

  public async getPins(): Promise<Pin[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let pins: Pin[] = [];

      // Get Synced Pins
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.pinTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];

        
        pins.push(
          Pin.fromDto({
            id: row['id'],
            secondaryid: undefined,
            extfileid: row['extfileid'],
            pintypeid: row['pintypeid'],
            locationx: row['locationx'],
            locationy: row['locationy'],
            page: row['page'],
            name: row['name'],
            description: row['description'],
            color: row['color'],
            entitycreatedtstamp: row['entitycreatedtstamp'],
            createdby: row['createdby'],
            entitycreateddevicetoken: row['entitycreateddevicetoken'],
            entitycreatedlocaldeviceid: row['entitycreatedlocaldeviceid']
          }, row['databag'] ? JSON.parse(row['databag']) : undefined)
        );
      }

      // Get local generated Pins
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localPinTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < localData.values.length; i++) {
        let row = localData.values[i];
        pins.push(
          Pin.fromDto({
            id: undefined,
            secondaryid: row['id'],
            extfileid: row['extfileid'],
            pintypeid: row['pintypeid'],
            locationx: row['locationx'],
            locationy: row['locationy'],
            page: row['page'],
            name: row['name'],
            description: row['description'],
            color: row['color'],
            entitycreatedtstamp: row['entitycreatedtstamp'],
            createdby: row['createdby'],
            entitycreateddevicetoken: row['entitycreateddevicetoken'],
            entitycreatedlocaldeviceid: row['entitycreatedlocaldeviceid']
          }, row['databag'] ? JSON.parse(row['databag']) : undefined)
        );
      }

      return pins;
    } catch (error) {
      this.logger.log('Error reading pins from db', error);
      return undefined
    }
  }

  public async reset() {
    // Do nothing
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.pinTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.pinTableDefinition.tableName);
    this.logger.log(this.constructor.name, "truncate table", this.localPinTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.localPinTableDefinition.tableName);
  }
}
